import { Input, message, Button } from 'antd';
import { useEffect, useState, useMemo } from 'react';
import { Modal } from './antd-wrapper/modal/global/modal';
import { globalModal } from './antd-wrapper/modal/global';
import { agentMcpService } from '@/services/agent-mcp-service';
import type { McpParamDetail } from '@/services/agent-mcp-service/types';
import { toast } from 'sonner';
import { useNewStores } from '@/store/modules/agent-store';
import { useI18n } from '@/hooks/use-i18n';

export default function AgentTokenConfigModal({
  isShow,
  onClose,
  onCloseAll,
  type,
  agentName,
  agentKey,
  mcpStatus,
}: {
  isShow: boolean;
  onClose: () => void;
  onCloseAll?: () => void;
  type: 'configure' | 'manage';
  agentName: string;
  agentKey: string;
  mcpStatus?: 'missing_key' | 'failed' | 'success' | null;
}) {
  const { t } = useI18n(['agent', 'common']);
  const { agentStore } = useNewStores();
  const [mcpList, setMcpList] = useState<McpParamDetail[]>([]);
  const [paramValues, setParamValues] = useState<
    Record<number, Record<string, any>>
  >({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMcpIds, setErrorMcpIds] = useState<Set<number>>(new Set());

  const hasEmptyParams = useMemo(() => {
    return mcpList.some((mcp) => {
      const params = mcp.param || {};
      return Object.keys(params).some((key) => {
        const value = paramValues[mcp.mcp_id]?.[key];
        return !value || value.toString().trim() === '';
      });
    });
  }, [mcpList, paramValues]);

  const handleLoadMcpParams = async () => {
    try {
      setLoading(true);
      const response = await agentMcpService.getMcpParam(agentKey);
      setMcpList(response.mcp_param || []);

      const initialValues: Record<number, Record<string, any>> = {};
      response.mcp_param?.forEach((mcp) => {
        initialValues[mcp.mcp_id] = mcp.param || {};
      });
      setParamValues(initialValues);

      if (mcpStatus === 'failed') {
        const failedMcpIds = new Set(
          response.mcp_param?.map((mcp) => mcp.mcp_id) || [],
        );
        setErrorMcpIds(failedMcpIds);
      }
    } catch (error) {
      console.error('加载MCP参数失败', error);
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (mcpId: number, key: string, value: any) => {
    setParamValues((prev) => ({
      ...prev,
      [mcpId]: {
        ...prev[mcpId],
        [key]: value,
      },
    }));

    setErrorMcpIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(mcpId);
      return newSet;
    });
  };

  const handleSave = async () => {
    const toastId = toast.loading(
      t('agent:tokenConfig.verifyingMcpConnectivity'),
    );

    try {
      setSaving(true);
      setErrorMcpIds(new Set());

      const mcpParams = mcpList.map((mcp) => ({
        mcp_id: String(mcp.mcp_id),
        param: paramValues[mcp.mcp_id] || {},
      }));

      await agentMcpService.setMcpParam(agentKey, mcpParams);
      toast.success(t('agent:tokenConfig.keyConfigSuccess'), {
        id: toastId,
      });
      await agentStore.handleCheckMcp();
      onClose();
    } catch (error) {
      console.error('保存MCP参数失败', error);

      const failedMcpIds = new Set(mcpList.map((mcp) => mcp.mcp_id));
      setErrorMcpIds(failedMcpIds);
      toast.error(t('agent:tokenConfig.keyConfigFailed'), {
        id: toastId,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = () => {
    globalModal({
      key: 'delete-all-mcp-keys',
      title: t('agent:tokenConfig.confirmDelete'),
      content: t('agent:tokenConfig.deleteAllKeysConfirm'),
      okText: t('common:confirm'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        const toastId = toast.loading(t('agent:tokenConfig.deletingAllKeys'));

        try {
          setSaving(true);

          await agentMcpService.deleteMcpParam(agentKey);
          // 重新加载以清空界面并检查MCP状态
          await Promise.all([
            handleLoadMcpParams(),
            agentStore.handleCheckMcp(),
          ]);
          toast.success(t('agent:tokenConfig.allKeysDeleted'), {
            id: toastId,
          });
          onClose?.();
          onCloseAll?.();
        } catch (error) {
          console.error('删除密钥失败', error);
          toast.error(t('agent:tokenConfig.deleteKeysFailed'), {
            id: toastId,
          });
        } finally {
          setSaving(false);
        }
      },
    });
  };

  useEffect(() => {
    if (isShow && agentKey) {
      handleLoadMcpParams();
    } else if (!isShow) {
      setErrorMcpIds(new Set());
    }
  }, [isShow, agentKey]);

  return (
    <Modal
      onOk={handleSave}
      open={isShow}
      height={'fit-content'}
      onCancel={onClose}
      width={400}
      title={`${type === 'configure' ? t('agent:tokenConfig.configure') : t('agent:tokenConfig.manage')}${t('agent:tokenConfig.key')}`}
      confirmLoading={saving}
      okButtonProps={{ disabled: hasEmptyParams }}
      footer={
        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={handleDeleteAll}
            disabled={saving || mcpList.length === 0}
            className="text-[#ff4d4f] hover:text-[#ff7875] disabled:text-gray-400 disabled:cursor-not-allowed text-[14px]"
            style={{
              cursor:
                saving || mcpList.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {t('agent:tokenConfig.deleteAllKeys')}
          </button>
          <div className="flex gap-2">
            <Button type="text" onClick={onClose} disabled={saving}>
              {t('common:cancel')}
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={hasEmptyParams}
            >
              {t('common:confirm')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex max-h-[480px] flex-col gap-[24px] mt-[8px] px-[24px] overflow-y-auto">
        {loading && mcpList.length === 0 && (
          <div className="text-center py-4">{t('common:loading')}</div>
        )}

        {!loading && mcpList.length === 0 && (
          <div className="text-center py-4 text-gray-400">
            {t('agent:tokenConfig.noMcpConfig')}
          </div>
        )}

        {mcpList.length > 0 &&
          mcpList.map((mcpItem) => {
            const hasError = errorMcpIds.has(mcpItem.mcp_id);
            return (
              <div className="w-full" key={mcpItem.mcp_id}>
                <div className="text-[#000000BF] text-[14px] mb-[8px] font-medium">
                  {mcpItem.mcp_name}
                </div>
                <div className="text-[#00000073] text-[12px] mb-[8px]">
                  {mcpItem.mcp_desc}
                </div>

                <div className="flex flex-col gap-[16px]">
                  {Object.entries(mcpItem.param || {}).map(([key, value]) => {
                    return (
                      <div className="flex gap-[8px] flex-col" key={key}>
                        <div className="text-[#00000073] text-[12px]">
                          key：{key}
                        </div>
                        <Input
                          className="w-full"
                          placeholder={t('agent:tokenConfig.enterValue')}
                          value={paramValues[mcpItem.mcp_id]?.[key] ?? value}
                          onChange={(e) =>
                            handleParamChange(
                              mcpItem.mcp_id,
                              key,
                              e.target.value,
                            )
                          }
                          status={hasError ? 'error' : undefined}
                        />
                        {hasError && (
                          <div className="text-[12px] text-[#ff4d4f]">
                            {t('agent:tokenConfig.mcpKeyInvalid')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
    </Modal>
  );
}
